import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Company } from '@app/pages/authenticated/pages/users/users.model';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CompanyService } from '@app/shared/services/company.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { Router } from '@angular/router';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { SsoConnectionService } from '@app/shared/services/sso-connection.service';
import { SsoConnectionListItem, ssoErrorMessage } from '@app/shared/models/sso-connection.model';
import { AlertSeverity } from '@app/shared/components/alert-banners/alert-banners.component';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';

export const SSO_CONNECTION_PERMISSION_TOOLTIP = 'Requires the Modify SSO Connections permission';
export const SSO_CONNECTION_HIDDEN_LABEL = 'Assigned — insufficient permission to view';
export const SSO_CONNECTION_UNASSIGNED_LABEL = 'Not assigned (falls back to Google)';

/** The company update body. Typed so a key typo cannot reach the wire as a silent no-op. */
interface CompanySettingsUpdate {
  enforceSSO: boolean;
  settings: Record<string, unknown>;
  ssoConnectionUuid?: string | null;
}

@Component({
  standalone: false,
  selector: 'app-company-settings',
  templateUrl: './company-settings.component.html',
})
export class CompanySettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() company: Company | undefined;
  @Input() user: AdminUser | undefined;
  @Output() refreshCompany: EventEmitter<string> = new EventEmitter<string>();

  public loading = true;
  public canEdit: boolean = false;
  /** Without VIEW_SSO_CONNECTIONS the option list cannot be fetched, so no picker can be rendered. */
  public canViewSsoConnections: boolean = false;
  /** Saving an assignment needs MODIFY_SSO_CONNECTIONS on top of the company-settings scope. */
  public canAssignSsoConnection: boolean = false;
  public ssoConnections: SsoConnectionListItem[] = [];
  public readonly ssoConnectionTooltip = SSO_CONNECTION_PERMISSION_TOOLTIP;
  public readonly ssoUnassignedNotification = {
    severity: AlertSeverity.WARNING,
    message:
      'SSO is enforced but no SSO connection is assigned. Users will be sent to Google sign-in instead of your identity provider.',
  };
  public companySettingsForm: FormGroup;
  private fraudMutualExclusiveSub = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private userService: UserService,
    private logger: LoggerService,
    private router: Router,
    private bannerNotificationsService: BannerNotificationsService,
    private ssoConnectionService: SsoConnectionService,
  ) {}
  ngOnInit() {
    this.loading = true;
    if (
      this.company &&
      this.user &&
      this.userService.hasAuthScope(this.user, [AdminScopes.VIEW_USERS_SETTINGS_COMPANY_SETTINGS])
    ) {
      this.canEdit = this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_USERS_SETTINGS_COMPANY_SETTINGS]);
      this.canViewSsoConnections = this.userService.hasAuthScope(this.user, [AdminScopes.VIEW_SSO_CONNECTIONS]);
      // Assigning without being able to list the options would leave "Not assigned" as the only
      // choice, i.e. a one-click unassign with no way back.
      this.canAssignSsoConnection =
        this.canEdit &&
        this.canViewSsoConnections &&
        this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_SSO_CONNECTIONS]);
      this.createForm();
      this.loadSsoConnections();
    } else {
      this.router.navigate(['users']);
    }
  }

  /** The parent re-fetches the company after a save; pick the stored assignment up from that. */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['company'] || !this.companySettingsForm) {
      return;
    }
    this.companySettingsForm.patchValue(
      {
        enforceSSO: this.company?.enforceSSO || false,
        ssoConnectionUuid: this.company?.ssoConnectionUuid || null,
      },
      { emitEvent: false },
    );
  }

  /** Shown instead of a picker the admin has no options for, so an assignment never reads as blank. */
  public get assignedSsoConnectionLabel(): string {
    const uuid = this.companySettingsForm?.get('ssoConnectionUuid')?.value;
    if (!uuid) {
      return SSO_CONNECTION_UNASSIGNED_LABEL;
    }
    const assigned = this.ssoConnections.find((connection) => connection.uuid === uuid);
    return assigned ? assigned.name : SSO_CONNECTION_HIDDEN_LABEL;
  }

  /** Empty while the admin can assign, so the tooltip only appears on the disabled picker. */
  public get ssoConnectionTooltipText(): string {
    return this.canAssignSsoConnection ? '' : this.ssoConnectionTooltip;
  }

  /** Enforcing SSO without an assigned connection silently falls back to Google, so it is called out. */
  public get showSsoUnassignedWarning(): boolean {
    if (!this.companySettingsForm) {
      return false;
    }
    return (
      !!this.companySettingsForm.get('enforceSSO')?.value && !this.companySettingsForm.get('ssoConnectionUuid')?.value
    );
  }

  /** Options come from the same list endpoint the SSO connections page uses; it needs the view scope. */
  private loadSsoConnections(): void {
    if (!this.canViewSsoConnections) {
      return;
    }
    this.ssoConnectionService.list().subscribe({
      next: (connections) => (this.ssoConnections = connections || []),
      error: (err) => {
        this.logger.error('Load SSO connections error', err);
        this.bannerNotificationsService.error(ssoErrorMessage(err, 'Failed to load the SSO connections'));
      },
    });
  }

  private createForm() {
    const companySettings = this.company.settings || {};
    this.companySettingsForm = this.formBuilder.group({
      enforceSSO: [this.company.enforceSSO || false],
      ssoConnectionUuid: [
        { value: this.company.ssoConnectionUuid || null, disabled: !this.canAssignSsoConnection },
      ],

      // London Drugs has two separate settings for EEKO and Trexity rates
      isLD: [companySettings.isLD || false],
      isLDTrexity: [companySettings.isLDTrexity || false],

      taxExempt: [companySettings.taxExempt || false],
      addBlindMarginAllowed: [companySettings.addBlindMarginAllowed || false],
      showCustomerReceipt: [companySettings.showCustomerReceipt || false],
      isFraudulent: [companySettings.isFraudulent || false],
      isSigneLocal: [companySettings.isSigneLocal || false],
      hasNoShipments: [companySettings.hasNoShipments || false],
      fraudWhitelist: [companySettings.fraudWhitelist || false],
    });
    this.normalizeFraudMutualExclusive();
    const fraudWhitelist = this.companySettingsForm.get('fraudWhitelist');
    const isFraudulent = this.companySettingsForm.get('isFraudulent');
    if (fraudWhitelist && isFraudulent) {
      this.fraudMutualExclusiveSub.add(
        fraudWhitelist.valueChanges.subscribe((whitelisted: boolean) => {
          if (whitelisted) {
            isFraudulent.setValue(false, { emitEvent: false });
          }
        }),
      );
      this.fraudMutualExclusiveSub.add(
        isFraudulent.valueChanges.subscribe((fraudulent: boolean) => {
          if (fraudulent) {
            fraudWhitelist.setValue(false, { emitEvent: false });
          }
        }),
      );
    }
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.fraudMutualExclusiveSub.unsubscribe();
  }

  /** If both flags are true from saved data, keep whitelist and clear suspected. */
  private normalizeFraudMutualExclusive(): void {
    const whitelisted = !!this.companySettingsForm.get('fraudWhitelist')?.value;
    const fraudulent = !!this.companySettingsForm.get('isFraudulent')?.value;
    if (whitelisted && fraudulent) {
      this.companySettingsForm.get('isFraudulent')?.setValue(false, { emitEvent: false });
    }
  }

  public submit() {
    if (this.canEdit && !this.companySettingsForm.invalid) {
      // ssoConnectionUuid is a column on the company, not a settings flag: destructuring it out of the
      // rest keeps it from being swallowed into the settings JSON blob.
      const { enforceSSO: updatedSSOSetting, ssoConnectionUuid, ...updatedSettings } = this.companySettingsForm.value;
      const updateObj: CompanySettingsUpdate = {
        enforceSSO: updatedSSOSetting,
        settings: {
          ...(this.company?.settings || {}),
          ...updatedSettings,
        },
      };
      // Sending the key at all requires MODIFY_SSO_CONNECTIONS, so omit it when the admin cannot assign.
      if (this.canAssignSsoConnection) {
        updateObj.ssoConnectionUuid = ssoConnectionUuid || null;
      }

      this.companyService.update(this.company.uuid, updateObj).subscribe(
        (response: any) => {
          this.refreshCompany.emit();
          this.logger.log('UPDATE Company Settings', response);
          this.bannerNotificationsService.success('Company settings flags have been updated');
        },
        (err: Error) => {
          this.bannerNotificationsService.error(
            `There was a problem updating the company settings flags. ${_.get(err, 'error.message', '')}`,
          );
          this.logger.error('UPDATE Company Settings error', err);
        },
      );
    }
  }
}
